import { useEffect, useRef, useState, startTransition, ViewTransition } from 'react';
import { DEFAULT_SOUND, type NotificationSound } from '@avslack/shared';
import { onAlertFired, onAlertResolved, type AlertEvent } from '../lib/alert-bus';
import { pushDeliversHere } from '../lib/push-device';
import { playNotificationSound } from '../lib/sound';
import { useNotificationPrefs } from '../data/notifications';
import { Icon } from './Icon';

/**
 * Riasztás - a szuperadminnak, ugyanúgy, mint az emlékeztető.
 *
 * A szerver csak a szuperadminoknak küldi; itt nem kell szűrni. A némítást
 * szándékosan nem nézzük: aki üzemelteti a rendszert, azt épp az érdekli,
 * hogy akkor is észrevegye, ha csendre állította magát.
 *
 * Ha a riasztás megszűnik, mielőtt bezárták, az ablak magától eltűnik - egy
 * már megoldódott hibáról ne kelljen külön dönteni.
 */

async function announce(event: AlertEvent, sound: NotificationSound): Promise<void> {
  const visible = typeof document !== 'undefined' && document.visibilityState === 'visible';
  if (visible) playNotificationSound(sound);

  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  // A riasztasrol a szerver MINDIG push-t is kuld - ahol az megerkezik, ott nem ketszerezunk.
  if (pushDeliversHere()) return;

  const options: NotificationOptions & { requireInteraction?: boolean } = {
    body: event.alert.summary,
    tag: `alert-${event.alert.fingerprint}`,
    icon: '/icons/icon-192.png',
    badge: '/icons/badge-72.png',
    silent: visible,
    requireInteraction: true,
  };

  try {
    const registration = await navigator.serviceWorker?.ready;
    if (registration) {
      await registration.showNotification(`Riasztás: ${event.alert.name}`, options);
      return;
    }
  } catch {
    // A service worker hianya nem indok arra, hogy ne jelezzunk sehogy.
  }

  try {
    new Notification(`Riasztás: ${event.alert.name}`, options);
  } catch {
    // Az ertesites hianya nem torheti el a felugro ablakot.
  }
}

export function AlertPopup() {
  const [queue, setQueue] = useState<AlertEvent[]>([]);

  const { data: prefs } = useNotificationPrefs();
  const soundRef = useRef<NotificationSound>(DEFAULT_SOUND);
  soundRef.current = prefs?.soundName ?? DEFAULT_SOUND;

  useEffect(() => {
    const offFired = onAlertFired((event) => {
      setQueue((current) =>
        current.some((item) => item.alert.fingerprint === event.alert.fingerprint)
          ? current
          : [...current, event],
      );
      void announce(event, soundRef.current);
    });
    const offResolved = onAlertResolved((fingerprint) => {
      startTransition(() =>
        setQueue((current) => current.filter((item) => item.alert.fingerprint !== fingerprint)),
      );
    });
    return () => {
      offFired();
      offResolved();
    };
  }, []);

  const current = queue[0];
  if (!current) return null;

  /*
    Atmenetkent: a lezart doboz lecsuszik, es ha van meg a sorban, a kovetkezo
    a sajat animaciojaval erkezik a helyere - latszik, hogy ez EGY MASIK.
  */
  const dismiss = () => startTransition(() => setQueue((items) => items.slice(1)));
  const critical = current.alert.severity === 'critical';
  const labels = Object.entries(current.alert.labels)
    .filter(([key]) => key !== 'job' && key !== 'instance')
    .map(([key, value]) => `${key}=${value}`)
    .join(' · ');

  return (
    <ViewTransition key={current.alert.fingerprint} exit="popup-out" default="none">
      <div
        className={`animate-fade-rise fixed bottom-4 right-4 z-[60] w-80 rounded-card border bg-surface-raised p-4 shadow-modal ${
          critical ? 'border-danger/60' : 'border-alert/50'
        }`}
      >
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 ${critical ? 'text-danger' : 'text-alert'}`}>
            <Icon name="figyelmeztetes" />
          </span>
          {/*
          A role="alert" CSAK a szovegen van, nem az egesz dobozon: igy a
          "meg N var" szamlalo valtozasa nem olvastatja fel ujra a riasztast,
          es a gombok felirata sem kerul bele a bejelentesbe. A FOKUSZT
          szandekosan nem vesszuk el - munka kozben ugrik fel.
        */}
          <div role="alert" className="min-w-0 flex-1">
            <p className="break-words font-display text-sm font-semibold">
              {critical ? 'Riasztás' : 'Figyelmeztetés'} · {current.alert.name}
            </p>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-ink">
              {current.alert.summary}
            </p>
            <p className="mt-2 break-words text-2xs text-ink-faint">
              {labels ? `${labels} · ` : ''}
              {new Date(current.alert.startedAt).toLocaleString('hu-HU', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="text-2xs text-ink-faint">
            {queue.length > 1 ? `még ${queue.length - 1} vár` : ''}
          </span>
          <div className="flex items-center gap-1.5">
            {current.grafanaUrl && (
              <a
                href={current.grafanaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-control px-2.5 py-1.5 text-sm text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
              >
                Grafana
              </a>
            )}
            <button
              type="button"
              onClick={dismiss}
              className="flex items-center gap-1.5 rounded-control bg-accent px-3 py-1.5 text-sm font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover"
            >
              <Icon name="pipa" size="sm" />
              Láttam
            </button>
          </div>
        </div>
      </div>
    </ViewTransition>
  );
}
